"use client";

import { usePathname } from "next/navigation";
import Link from "next/link";

export default function Footer({ settings }) {
  const siteName = settings?.siteName || "Prodigy Combat Academy";
  const contactEmail = settings?.contactEmail;
  const phoneNumber = settings?.phoneNumber;
  const address = settings?.address;
  const city = settings?.city;
  const state = settings?.state;
  const zipCode = settings?.zipCode;
  const footerText = settings?.footerText;
  const socialMedia = settings?.socialMedia || {};
  const businessHours = settings?.businessHours || {};
  const currentYear = new Date().getFullYear();
  const pathname = usePathname();
  const isAdminPage = pathname?.startsWith("/admin");
  const isDashboardPage = pathname?.startsWith("/dashboard");

  // Format full address
  const formatAddress = () => {
    const parts = [];
    if (address) parts.push(address);
    // Combine city, state, and zip code on one line: "Ceres, California 95357"
    if (city || state || zipCode) {
      const cityState = [city, state].filter(Boolean).join(", ");
      const cityStateZip = [cityState, zipCode].filter(Boolean).join(" ");
      if (cityStateZip) parts.push(cityStateZip);
    }
    return parts;
  };

  const handleLinkClick = (e, section) => {
    // If on home page, scroll to section after a brief delay to ensure page is ready
    if (pathname === "/" && !isAdminPage && !isDashboardPage) {
      e.preventDefault();
      setTimeout(() => {
        const sectionId = section.replace("#", "");
        const element = document.getElementById(sectionId);
        if (element) {
          element.scrollIntoView({ behavior: "smooth" });
        }
      }, 100);
    }
    // If on other pages (like /waiver), let Link handle navigation normally
    // If on admin/dashboard page, Link will handle navigation to home page with hash
  };

  // Check if there are any business hours to display
  const hasBusinessHours = businessHours && Object.values(businessHours).some(hours => hours && hours.trim());

  return (
    <footer className="bg-black text-gray-300">
      <div className="container mx-auto px-4 py-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">

          {/* Brand */}
          <div>
            <div className="text-2xl font-bold text-white mb-4">
              <span className="text-[#D4AF37]">{siteName}</span>
            </div>
            <p className="text-sm">
              {footerText ||
                "Your trusted fitness partner. We help you achieve your fitness goals with expert training, state-of-the-art equipment, and a supportive community."}
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-white font-semibold mb-4">Quick Links</h3>
            <ul className="space-y-2 text-sm">
              <li>
                <Link
                  href="/#home"
                  onClick={(e) => handleLinkClick(e, "#home")}
                  className="hover:text-[#D4AF37] transition-colors cursor-pointer"
                >
                  Home
                </Link>
              </li>
              <li>
                <Link
                  href="/#classes"
                  onClick={(e) => handleLinkClick(e, "#classes")}
                  className="hover:text-[#D4AF37] transition-colors cursor-pointer"
                >
                  Classes
                </Link>
              </li>
              <li>
                <Link
                  href="/#schedule"
                  onClick={(e) => handleLinkClick(e, "#schedule")}
                  className="hover:text-[#D4AF37] transition-colors cursor-pointer"
                >
                  Schedule
                </Link>
              </li>
              <li>
                <Link
                  href="/#about"
                  onClick={(e) => handleLinkClick(e, "#about")}
                  className="hover:text-[#D4AF37] transition-colors cursor-pointer"
                >
                  About
                </Link>
              </li>
              <li>
                <Link
                  href="/#contact"
                  onClick={(e) => handleLinkClick(e, "#contact")}
                  className="hover:text-[#D4AF37] transition-colors cursor-pointer"
                >
                  Contact
                </Link>
              </li>
            </ul>

            <h3 className="text-white font-semibold mt-8 mb-4">Legal</h3>
            <ul className="space-y-2 text-sm">
              <li>
                <Link
                  href="/privacy-policy"
                  className="hover:text-[#D4AF37] transition-colors"
                >
                  Privacy Policy
                </Link>
              </li>
              <li>
                <Link
                  href="/terms"
                  className="hover:text-[#D4AF37] transition-colors"
                >
                  Terms &amp; Conditions
                </Link>
              </li>
              <li>
                <Link
                  href="/cookie-policy"
                  className="hover:text-[#D4AF37] transition-colors"
                >
                  Cookie Policy
                </Link>
              </li>
              <li>
                <Link
                  href="/refund-policy"
                  className="hover:text-[#D4AF37] transition-colors"
                >
                  Refund Policy
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="text-white font-semibold mb-4">Contact Info</h3>
            <ul className="space-y-2 text-sm">
              {formatAddress().map((line, index) => (
                <li key={index}>{line}</li>
              ))}
              {phoneNumber && (
                <li className="pt-2">
                  <a
                    href={`tel:${phoneNumber.replace(/\D/g, "")}`}
                    className="hover:text-[#D4AF37] transition-colors"
                  >
                    {phoneNumber}
                  </a>
                </li>
              )}
              {contactEmail && (
                <li>
                  <a
                    href={`mailto:${contactEmail}`}
                    className="hover:text-[#D4AF37] transition-colors"
                  >
                    {contactEmail}
                  </a>
                </li>
              )}
              {!phoneNumber &&
                !contactEmail &&
                formatAddress().length === 0 && (
                  <li className="text-gray-500">
                    No contact information available
                  </li>
                )}
            </ul>
            
            {/* Business Hours */}
            {hasBusinessHours && (
              <>
                <h3 className="text-white font-semibold mt-8 mb-4">Business Hours</h3>
                <ul className="space-y-2 text-sm">
                  {[
                    { key: "monday", label: "Monday" },
                    { key: "tuesday", label: "Tuesday" },
                    { key: "wednesday", label: "Wednesday" },
                    { key: "thursday", label: "Thursday" },
                    { key: "friday", label: "Friday" },
                    { key: "saturday", label: "Saturday" },
                    { key: "sunday", label: "Sunday" },
                  ].map(({ key, label }) => {
                    const hours = businessHours[key];
                    if (!hours || !hours.trim()) return null;
                    return (
                      <li key={key} className="flex justify-between">
                        <span className="text-gray-400">{label}:</span>
                        <span className="text-gray-300">{hours}</span>
                      </li>
                    );
                  })}
                </ul>
              </>
            )}
          </div>

          {/* Social Media */}
          <div>
            <h3 className="text-white font-semibold mb-4">Follow Us</h3>
            <div className="flex space-x-4">
              {socialMedia.facebook && (
                <a
                  href={socialMedia.facebook}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-[#D4AF37] transition-colors"
                  aria-label="Facebook"
                >
                  <svg
                    className="w-6 h-6"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                  </svg>
                </a>
              )}
              {socialMedia.instagram && (
                <a
                  href={socialMedia.instagram}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-[#D4AF37] transition-colors"
                  aria-label="Instagram"
                >
                  <svg
                    className="w-6 h-6"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
                  </svg>
                </a>
              )}
              {socialMedia.twitter && (
                <a
                  href={socialMedia.twitter}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-[#D4AF37] transition-colors"
                  aria-label="Twitter"
                >
                  <svg
                    className="w-6 h-6"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z" />
                  </svg>
                </a>
              )}
              {socialMedia.youtube && (
                <a
                  href={socialMedia.youtube}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-[#D4AF37] transition-colors"
                  aria-label="YouTube"
                >
                  <svg
                    className="w-6 h-6"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
                  </svg>
                </a>
              )}
              {!socialMedia.facebook &&
                !socialMedia.instagram &&
                !socialMedia.twitter &&
                !socialMedia.youtube && (
                  <p className="text-sm text-gray-500">
                    No social media links available
                  </p>
                )}
            </div>
          </div>
        </div>
        <div className="border-t border-gray-800 mt-8 pt-8 text-center text-sm">
          <p>
            &copy; {currentYear} {siteName}. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
}
